import React, { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { SEO_CONFIG } from '../config/seo';
import { findHoa, MARICOPA_HOAS } from '../config/hoas';
import './HOAPages.css';

const HOAPage = () => {
  const { slug } = useParams();
  const item = findHoa(slug);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.body.classList.remove('intro-active');
  }, [slug]);

  if (!item) {
    return <main className="hoa-page"><div className="hoa-shell hoa-not-found"><Link to="/maricopa-hoas">← HOA directory</Link><h1>HOA guide not found</h1></div></main>;
  }

  const related = MARICOPA_HOAS.filter((hoa) => hoa.slug !== item.slug).slice(0, 4);
  return (
    <main className="hoa-page">
      <div className="hoa-shell">
        <Link className="hoa-back" to="/maricopa-hoas">← All Maricopa HOAs</Link>
        <header className="hoa-hero">
          <span>Maricopa HOA guide</span>
          <h1>{item.name}</h1>
          {item.aliases?.length > 0 && <p>Also referenced as {item.aliases.join(', ')}</p>}
        </header>

        <section className="hoa-overview">
          <div>
            <h2>Community overview</h2>
            <p>{item.overview}</p>
          </div>
          <aside className="hoa-manager-card">
            <span>Directory management contact</span>
            <h2>{item.manager.name}</h2>
            <a href={`tel:${item.manager.phone.replace(/[^+\d]/g, '')}`}>{item.manager.phone}</a>
            {item.manager.url && <a href={item.manager.url} target="_blank" rel="noopener noreferrer">Management website ↗</a>}
            <small>Confirm this assignment before sending money or personal documents.</small>
          </aside>
        </section>

        <section className="hoa-fees">
          <div className="hoa-section-heading">
            <span>Historical reference</span>
            <h2>Assessment and closing-cost snapshot</h2>
          </div>
          <div className="hoa-notice"><strong>Not a current fee quote.</strong> {item.feeSourceNote}</div>
          <div className="hoa-fee-table" role="table" aria-label={`${item.name} historical HOA fees`}>
            {item.feeSnapshot.map(([label, value]) => (
              <div className="hoa-fee-row" role="row" key={`${label}-${value}`}>
                <span role="cell">{label}</span><strong role="cell">{value}</strong>
              </div>
            ))}
          </div>
        </section>

        <section className="hoa-due-diligence">
          <div>
            <span className="hoa-kicker">Before you buy</span>
            <h2>Items to verify for this community</h2>
            <ul>{item.researchTopics.map((topic) => <li key={topic}>{topic}</li>)}</ul>
          </div>
          <div>
            <span className="hoa-kicker">Always request</span>
            <h2>HOA document checklist</h2>
            <ul>
              <li>Current resale disclosure and payoff statement</li>
              <li>CC&amp;Rs, bylaws, rules, and architectural guidelines</li>
              <li>Current budget, most recent financials, and reserve study</li>
              <li>Pending special assessments, litigation, or major projects</li>
              <li>Insurance summary and recent board-meeting minutes</li>
            </ul>
          </div>
        </section>

        <section className="hoa-sources">
          <h2>Research sources</h2>
          <p>Sources are provided for direct verification. Association portals may require an owner login.</p>
          <ul>{item.sources.map((source) => <li key={source.url}><a href={source.url} target="_blank" rel="noopener noreferrer">{source.label} ↗</a></li>)}</ul>
          <p className="hoa-source-note">Fee source: undated City of Maricopa “Directory of Property Management Companies” PDF supplied by the site owner. The PDF states its figures are estimates and subject to change.</p>
        </section>

        <section className="hoa-cta">
          <span>Buying or selling in {item.name}?</span>
          <h2>Get help reviewing the property-specific HOA package.</h2>
          <div><a href={`mailto:${SEO_CONFIG.AGENT_EMAIL}?subject=${encodeURIComponent(`${item.name} HOA question`)}`}>Email {SEO_CONFIG.AGENT_NAME}</a><a href={`tel:${SEO_CONFIG.AGENT_PHONE}`}>Call {SEO_CONFIG.AGENT_PHONE}</a></div>
        </section>

        <nav className="hoa-related" aria-label="More HOA guides">
          <h2>More Maricopa HOA guides</h2>
          <div>{related.map((hoa) => <Link key={hoa.slug} to={`/maricopa-hoas/${hoa.slug}`}>{hoa.name}</Link>)}</div>
        </nav>
      </div>
    </main>
  );
};

export default HOAPage;
